/**
 * /app/$tenantSlug/settings/shifts — Settings → Time & Leave → Shifts
 * (HR-admin). Shift definition CRUD. Times are entered as HH:MM and stored as
 * minutes-since-midnight. is_night_shift is derived server-side (end <= start).
 */

import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { toast } from "sonner";
import { SettingsPageHeader } from "@/components/shared/settings-page-header";
import { SettingsShell } from "@/components/shared/settings-shell";
import { SETTINGS_GROUPS } from "@/lib/settings-nav";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { useShifts, useCreateShift, useDeleteShift } from "@/hooks/use-shifts";

export const Route = createFileRoute(
  "/_authgated/app/$tenantSlug/settings/shifts",
)({
  component: SettingsShiftsRoute,
});

function hhmmToMinute(s: string): number | null {
  const m = /^(\d{1,2}):(\d{2})$/.exec(s.trim());
  if (!m) return null;
  const h = Number(m[1]);
  const min = Number(m[2]);
  if (h > 23 || min > 59) return null;
  return h * 60 + min;
}

function minuteToHHMM(m: number): string {
  return `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;
}

function SettingsShiftsRoute() {
  const shifts = useShifts(true);
  const create = useCreateShift();
  const del = useDeleteShift();

  const [name, setName] = useState("");
  const [start, setStart] = useState("09:00");
  const [end, setEnd] = useState("18:00");
  const [brk, setBrk] = useState("60");
  const [grace, setGrace] = useState("10");

  async function add() {
    const startMin = hhmmToMinute(start);
    const endMin = hhmmToMinute(end);
    if (!name.trim() || startMin === null || endMin === null) {
      toast.error("Name and valid HH:MM start/end are required");
      return;
    }
    try {
      await create.mutateAsync({
        name: name.trim(),
        start_minute: startMin,
        end_minute: endMin,
        break_minutes: Number(brk) || 0,
        grace_minutes: Number(grace) || 0,
      });
      toast.success("Shift created");
      setName("");
    } catch {
      toast.error("Failed to create shift (name may be taken)");
    }
  }

  return (
    <>
      <SettingsPageHeader />
      <SettingsShell groups={SETTINGS_GROUPS}>
        <div className="space-y-6">
          <div>
            <h2 className="text-lg font-semibold">Shift definitions</h2>
            <p className="text-muted-foreground text-sm">
              Define shifts (start/end, break, grace). Night shifts crossing
              midnight are detected automatically.
            </p>
          </div>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">New shift</CardTitle>
            </CardHeader>
            <CardContent className="grid items-end gap-3 sm:grid-cols-6">
              <div className="sm:col-span-2">
                <Label htmlFor="sn">Name</Label>
                <Input id="sn" value={name} onChange={(e) => setName(e.target.value)} />
              </div>
              <div>
                <Label htmlFor="ss">Start</Label>
                <Input id="ss" value={start} onChange={(e) => setStart(e.target.value)} placeholder="09:00" />
              </div>
              <div>
                <Label htmlFor="se">End</Label>
                <Input id="se" value={end} onChange={(e) => setEnd(e.target.value)} placeholder="18:00" />
              </div>
              <div>
                <Label htmlFor="sb">Break (m)</Label>
                <Input id="sb" value={brk} onChange={(e) => setBrk(e.target.value)} />
              </div>
              <div>
                <Label htmlFor="sg">Grace (m)</Label>
                <Input id="sg" value={grace} onChange={(e) => setGrace(e.target.value)} />
              </div>
              <Button className="sm:col-span-6 sm:w-32" onClick={add} disabled={create.isPending}>
                Create shift
              </Button>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Shifts</CardTitle>
            </CardHeader>
            <CardContent>
              {shifts.data && shifts.data.shifts.length > 0 ? (
                <ul className="divide-y text-sm">
                  {shifts.data.shifts.map((s) => (
                    <li key={s.id} className="flex items-center justify-between py-2">
                      <span className="flex items-center gap-2">
                        <span className="font-medium">{s.name}</span>
                        <span className="text-muted-foreground">
                          {minuteToHHMM(s.start_minute)}–{minuteToHHMM(s.end_minute)}
                        </span>
                        {s.is_night_shift && <Badge variant="secondary">night</Badge>}
                        {!s.is_active && <Badge variant="outline">inactive</Badge>}
                      </span>
                      {s.is_active && (
                        <Button size="sm" variant="ghost" onClick={() => del.mutate(s.id)}>
                          Deactivate
                        </Button>
                      )}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-muted-foreground text-sm">No shifts defined.</p>
              )}
            </CardContent>
          </Card>
        </div>
      </SettingsShell>
    </>
  );
}
